// The desk's sidebar, as in the screenshots: logo and a menu button that collapses the whole bar to icons, a CMS group
// that folds, the screens, a PREFERENCES group, and -- where LeadXP has the email and Sign Out -- the role chooser.
// The role chooser stands in for sign-in until real roles exist; it is the only place that changes role.
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useHostIdentity, useHostMenuExtras } from 'src/front-components/lead-desk/host';
import { Icon, type IconName } from 'src/front-components/lead-desk/icons';
import { DeskSelect } from 'src/front-components/theme/desk-controls';
import { BRAND } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import { ROLE_PROFILES, screenLabel, isAdmin, isSuperAdmin, type DeskRole, type DeskScreen } from 'src/utils/lead-desk';

export const SCREEN_ICONS: Record<DeskScreen, IconName> = {
  OVERVIEW: 'dashboard',
  HOME: 'home',
  STUDENTS: 'users',
  ADD: 'plus',
  USERS: 'userCheck',
  REPORTS: 'report',
  WEB_LEADS: 'globe',
  FOLDERS: 'folder',
  ACCOUNT: 'user',
};

const ROLES: DeskRole[] = ['ADMIN', 'COUNSELLOR', 'TELECALLER'];
const EXPANDED = 224;
const COLLAPSED = 60;

const SectionLabel = ({ scheme, children }: { scheme: string; children: ReactNode }) => (
  <div
    style={{
      ...SHELL_TYPE.caption,
      fontWeight: 600,
      color: getShellColors(scheme).textTertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      margin: `${SHELL_SPACE[4]} ${SHELL_SPACE[2]} ${SHELL_SPACE[1]}`,
    }}
  >
    {children}
  </div>
);

const NavButton = ({
  scheme,
  icon,
  label,
  active,
  collapsed,
  onClick,
  trailing,
  outlined = false,
}: {
  scheme: string;
  icon: IconName;
  label: string;
  active?: boolean;
  collapsed: boolean;
  onClick: () => void;
  trailing?: ReactNode;
  outlined?: boolean;
}) => {
  const shell = getShellColors(scheme);

  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-current={active === true ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: SHELL_SPACE[2],
        width: '100%',
        padding: `${SHELL_SPACE[1.5]} ${collapsed ? SHELL_SPACE[1] : SHELL_SPACE[2]}`,
        marginBottom: SHELL_SPACE.half,
        cursor: 'pointer',
        borderRadius: SHELL_RADIUS.sm,
        border: outlined ? `1px solid ${shell.borderMedium}` : '1px solid transparent',
        background: active === true ? v('background-transparent-light') : outlined ? shell.background : 'transparent',
        color: active === true ? v('color-blue') : shell.text,
        ...SHELL_TYPE.body,
        fontWeight: active === true || outlined ? 500 : 400,
        textAlign: 'left',
      }}
    >
      <Icon name={icon} size={collapsed ? 18 : 15} />
      {!collapsed && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
      {!collapsed && trailing}
    </button>
  );
};

/**
 * The account control at the foot of the sidebar: one compact button that opens a menu upward.
 *
 *   signed in (the host gave an identity): the button shows the person and their role. The menu has Sign out, the host's
 *   extras (the theme row) and, for an admin only, a small "Preview as" list to look at the counsellor and telecaller views.
 *   not signed in (inside the CRM): the old "View as" chooser, until real roles are wired to the CRM's workspace members.
 *
 * It is drawn with fixed positioning from the button's own position, so the collapsed 60px sidebar does not clip it.
 */
const AccountMenu = ({
  scheme,
  role,
  actingAs,
  people,
  collapsed,
  previewing,
  onRole,
  onPerson,
}: {
  scheme: string;
  role: DeskRole;
  actingAs: string;
  people: string[];
  collapsed: boolean;
  /** True when an admin is previewing another role's view. */
  previewing: boolean;
  onRole: (role: DeskRole) => void;
  onPerson: (name: string) => void;
}) => {
  const shell = getShellColors(scheme);
  const identity = useHostIdentity();
  const extras = useHostMenuExtras();
  const viewRole = ROLE_PROFILES[role];
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ left: number; bottom: number } | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const place = (): void => {
    const rect = trigger.current?.getBoundingClientRect();

    if (rect !== undefined) setAt({ left: Math.max(8, rect.left), bottom: window.innerHeight - rect.top + 6 });
  };

  useEffect(() => {
    if (!open) return undefined;

    const outside = (event: Event): void => {
      const target = event.target;

      if (target instanceof Node && !menu.current?.contains(target) && !trigger.current?.contains(target)) setOpen(false);
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };

    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', place);

    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const title = identity !== null ? identity.name : viewRole.label;
  const subtitle = identity !== null ? (previewing ? `Previewing ${viewRole.label}` : (ROLE_PROFILES[identity.role]?.label ?? identity.role)) : actingAs !== '' ? actingAs : 'View as';
  const initial = (title.trim()[0] ?? '?').toUpperCase();
  const label = identity !== null ? `Account menu, ${identity.name}, ${ROLE_PROFILES[identity.role].label}` : `Viewing as ${viewRole.label}${actingAs !== '' ? `, ${actingAs}` : ''}`;
  const caption = { ...SHELL_TYPE.caption, fontWeight: 600, color: shell.textTertiary, textTransform: 'uppercase' as const, letterSpacing: '0.06em', margin: `0 ${SHELL_SPACE[1]} ${SHELL_SPACE[1]}` };
  const row = (on: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: SHELL_SPACE[2],
    width: '100%',
    padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
    border: 'none',
    borderRadius: SHELL_RADIUS.sm,
    cursor: 'pointer',
    textAlign: 'left',
    ...SHELL_TYPE.body,
    fontWeight: on ? 600 : 400,
    background: on ? v('background-transparent-light') : 'transparent',
    color: shell.text,
  });

  return (
    <div style={{ borderTop: `1px solid ${shell.borderMedium}`, paddingTop: SHELL_SPACE[2], marginTop: SHELL_SPACE[2] }}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={collapsed ? label : undefined}
        onClick={() => {
          place();
          setOpen(!open);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: SHELL_SPACE[2],
          width: '100%',
          padding: SHELL_SPACE[1],
          border: 'none',
          borderRadius: SHELL_RADIUS.sm,
          background: open ? v('background-transparent-light') : 'transparent',
          color: shell.text,
          cursor: 'pointer',
          textAlign: 'left',
          ...SHELL_TYPE.body,
        }}
      >
        <span
          aria-hidden="true"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: v('color-blue'), color: '#fff', fontWeight: 600, fontSize: 12 }}
        >
          {initial}
        </span>
        {!collapsed && (
          <>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', ...SHELL_TYPE.bodyMedium, color: shell.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
              <span style={{ display: 'block', ...SHELL_TYPE.caption, color: previewing ? v('color-blue') : shell.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</span>
            </span>
            <Icon name="chevronUp" size={13} style={{ color: shell.textTertiary }} />
          </>
        )}
      </button>

      {open && at !== null && (
        <div
          ref={menu}
          role="menu"
          aria-label="Account"
          style={{
            position: 'fixed',
            left: at.left,
            bottom: at.bottom,
            zIndex: 80,
            width: 216,
            maxHeight: `calc(100vh - ${at.bottom + 16}px)`,
            overflowY: 'auto',
            padding: SHELL_SPACE[1.5],
            background: shell.background,
            border: `1px solid ${shell.borderMedium}`,
            borderRadius: SHELL_RADIUS.md,
            boxShadow: v('box-shadow-strong'),
          }}
        >
          {identity !== null && (
            <div style={{ padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]} ${SHELL_SPACE[2]}` }}>
              <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{identity.name}</div>
              <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{identity.email}</div>
            </div>
          )}

          {/* An admin may look at the other views; nobody else is offered a choice, and the server ignores a request for one. */}
          {(identity === null || isAdmin(identity.role)) && (
            <>
              <div style={caption}>{identity === null ? 'View as' : 'Preview as'}</div>
              <div role="radiogroup" aria-label={identity === null ? 'View as' : 'Preview as'} style={{ display: 'flex', flexDirection: 'column' }}>
                {(identity === null
                  ? ['ADMIN', 'COUNSELLOR', 'TELECALLER']
                  : isSuperAdmin(identity.role)
                    ? ['SUPER_ADMIN', 'ADMIN', 'COUNSELLOR', 'TELECALLER']
                    : ['ADMIN', 'COUNSELLOR', 'TELECALLER']
                ).map((r) => {
                  const on = r === role;

                  return (
                    <button
                      key={r}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => {
                        onRole(r as DeskRole);
                        setOpen(false);
                      }}
                      style={row(on)}
                    >
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: on ? v('color-blue') : shell.borderStrong }} />
                      {ROLE_PROFILES[r as DeskRole]?.label ?? r}
                    </button>
                  );
                })}
              </div>

              {role !== (identity?.role ?? 'ADMIN') && (
                <div style={{ marginTop: SHELL_SPACE[2] }}>
                  <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, margin: `0 ${SHELL_SPACE[1]} ${SHELL_SPACE.half}` }}>{identity === null ? 'Signed in as' : 'Looking at'}</div>
                  <DeskSelect
                    scheme={scheme}
                    value={actingAs}
                    onChange={(name) => {
                      onPerson(name);
                      setOpen(false);
                    }}
                    options={people.length === 0 ? [{ value: '', label: 'Nobody yet' }] : people.map((p) => ({ value: p, label: p }))}
                  />
                </div>
              )}
            </>
          )}

          {extras !== null && extras !== undefined && <div style={{ marginTop: SHELL_SPACE[2], paddingTop: SHELL_SPACE[2], borderTop: `1px solid ${shell.borderMedium}` }}>{extras}</div>}

          {identity !== null && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                identity.onSignOut();
              }}
              style={{ ...row(false), marginTop: SHELL_SPACE[2], paddingTop: SHELL_SPACE[1.5], paddingBottom: SHELL_SPACE[1.5], borderTop: `1px solid ${shell.borderMedium}`, borderRadius: 0, color: v('color-red') }}
            >
              <Icon name="logout" size={14} />
              Sign out
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({
  scheme,
  role,
  active,
  collapsed,
  groupOpen,
  actingAs,
  people,
  previewing,
  slideOver = false,
  onToggleCollapsed,
  onToggleGroup,
  onGo,
  onRole,
  onPerson,
}: {
  scheme: string;
  role: DeskRole;
  active: DeskScreen;
  collapsed: boolean;
  groupOpen: boolean;
  actingAs: string;
  people: string[];
  previewing: boolean;
  /** Drawn inside a slide-over on a narrow window: full height, no sticky offset, and the menu button closes it. */
  slideOver?: boolean;
  onToggleCollapsed: () => void;
  onToggleGroup: () => void;
  onGo: (screen: DeskScreen) => void;
  onRole: (role: DeskRole) => void;
  onPerson: (name: string) => void;
}) => {
  const shell = getShellColors(scheme);
  const profile = ROLE_PROFILES[role];
  const cms = profile.screens.filter((s) => s !== 'ACCOUNT');

  return (
    <nav
      aria-label={`${profile.label} menu`}
      style={{
        width: slideOver ? '100%' : collapsed ? COLLAPSED : EXPANDED,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        // Fixed to the viewport and sticky, so a long page on the right scrolls past it instead of making it taller.
        alignSelf: 'flex-start',
        position: slideOver ? 'static' : 'sticky',
        top: SHELL_SPACE[6],
        height: slideOver ? '100%' : 'calc(100vh - 2 * var(--t-spacing-6))',
        overflowY: 'auto',
        padding: SHELL_SPACE[2],
        borderRight: `1px solid ${shell.borderMedium}`,
        background: shell.backgroundSecondary,
        borderRadius: SHELL_RADIUS.md,
        transition: 'width 160ms ease',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: SHELL_SPACE[1],
          padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[1]} ${SHELL_SPACE[3]}`,
          minHeight: 36,
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        {!collapsed ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
            <img src="/ewings-logo-light.svg" alt="eWings" className="ewings-brand-logo-light" style={{ height: 28, width: 'auto', maxWidth: 125, objectFit: 'contain' }} />
            <img src="/ewings-logo-dark.svg" alt="eWings" className="ewings-brand-logo-dark" style={{ height: 28, width: 'auto', maxWidth: 125, objectFit: 'contain' }} />
          </div>
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="eWings">
            <img src="/favicon.svg" alt="eWings" style={{ width: 22, height: 22, objectFit: 'contain' }} />
          </div>
        )}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={slideOver ? 'Close the menu' : collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          aria-expanded={!collapsed}
          title={slideOver ? 'Close the menu' : collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          style={{
            display: 'inline-flex',
            padding: SHELL_SPACE[1],
            cursor: 'pointer',
            border: `1px solid ${shell.borderMedium}`,
            borderRadius: SHELL_RADIUS.sm,
            background: shell.background,
            color: shell.textSecondary,
            flexShrink: 0,
          }}
        >
          <Icon name="menu" size={15} />
        </button>
      </div>

      <NavButton
        scheme={scheme}
        icon="dashboard"
        label="CMS"
        collapsed={collapsed}
        outlined
        onClick={collapsed ? onToggleCollapsed : onToggleGroup}
        trailing={<Icon name={groupOpen ? 'chevronDown' : 'chevronRight'} size={13} />}
      />

      {(groupOpen || collapsed) && (
        <div style={{ marginTop: SHELL_SPACE[1], paddingLeft: collapsed ? 0 : SHELL_SPACE[2] }}>
          {cms.map((s) => (
            <NavButton key={s} scheme={scheme} icon={SCREEN_ICONS[s]} label={screenLabel(role, s)} active={active === s} collapsed={collapsed} onClick={() => onGo(s)} />
          ))}
        </div>
      )}

      {!collapsed && <SectionLabel scheme={scheme}>Preferences</SectionLabel>}
      {collapsed && <div style={{ height: SHELL_SPACE[3] }} />}
      <NavButton scheme={scheme} icon={SCREEN_ICONS.ACCOUNT} label={screenLabel(role, 'ACCOUNT')} active={active === 'ACCOUNT'} collapsed={collapsed} onClick={() => onGo('ACCOUNT')} />

      <div style={{ flex: 1 }} />

      {/* Role chooser, in the place LeadXP keeps the signed-in user and Sign Out. Opens upward. */}
      <AccountMenu scheme={scheme} role={role} actingAs={actingAs} people={people} collapsed={collapsed} previewing={previewing} onRole={onRole} onPerson={onPerson} />
    </nav>
  );
};

export const SIDEBAR_KEY = 'ewings-desk-sidebar';
